import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { loginSchema, signupSchema } from "@/lib/validations";
import { Footer } from "@/components/Footer";
import { ImageCropDialog } from "@/components/ImageCropDialog";
import { ClubSearchSelect } from "@/components/ClubSearchSelect";
import { getAppBranding } from "@/hooks/use-club-context";
import { StatusIndicator } from "@/components/StatusIndicator";
import { useTranslation } from "@/i18n/LanguageContext";
import { 
  trackSignUp, 
  trackLogin, 
  trackLoginStart,
  trackPasswordResetRequest,
  trackProfilePhotoUpload,
  trackPurchase,
} from "@/lib/analytics";

interface Club {
  id: string;
  name: string;
  city?: string;
  logo_url: string | null;
  has_active_subscription?: boolean;
}


const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClubId = searchParams.get('club');
  const preselectedTab = searchParams.get('tab');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(preselectedTab === "signup" || preselectedClubId ? "signup" : "login");
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [signupData, setSignupData] = useState({ 
    email: "", 
    password: "", 
    confirmPassword: "",
    firstName: "",
    lastName: "",
    gender: "",
    skillLevel: "",
    birthDate: "",
    clubId: preselectedClubId || "",
    confirmMember: false,
    acceptTerms: false,
    sports: ["tennis"] as ("tennis" | "padel")[]
  });
  const [selectedClubSports, setSelectedClubSports] = useState<("tennis" | "padel")[]>(["tennis"]);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [croppedPhoto, setCroppedPhoto] = useState<Blob | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [tempImageSrc, setTempImageSrc] = useState<string | null>(null);
  
  const branding = getAppBranding(null);
  const { t } = useTranslation();
  
  useEffect(() => {
    checkUser();
    loadClubs();
  }, []);

  // Track successful purchase on redirect from Mollie
  useEffect(() => {
    if (searchParams.get('registration') === 'success') {
      const checkoutData = sessionStorage.getItem('hussel_checkout');
      if (checkoutData) {
        try {
          const data = JSON.parse(checkoutData);
          trackPurchase({
            transactionId: `reg_${Date.now()}`,
            clubName: data.clubName || 'unknown',
            pricingTier: data.pricingTier || 'medium',
            priceExclBTW: data.priceExclBTW || 24.95,
            coupon: data.coupon,
            discount: data.discount,
          });
          sessionStorage.removeItem('hussel_checkout');
        } catch (e) {
          console.error('Error parsing checkout data for GA4 purchase:', e);
        }
      } else {
        trackPurchase({
          transactionId: `reg_${Date.now()}`,
          clubName: 'unknown',
          pricingTier: 'medium',
          priceExclBTW: 24.95,
        });
      }
    }
  }, [searchParams]);

  const loadClubs = async () => {
    try {
      const { data, error } = await supabase
        .from("clubs_public")
        .select("id, name, city, logo_url, sports, has_active_subscription")
        .eq("is_active", true)
        .eq("has_active_subscription", true)
        .order("name");

      if (error) throw error;
      setClubs((data || []) as unknown as Club[]);
    } catch (error) {
      console.error("Error loading clubs:", error);
    }
  };

  useEffect(() => {
    if (signupData.clubId) {
      const selectedClub = clubs.find(c => c.id === signupData.clubId);
      const clubSports = (selectedClub as any)?.sports as ("tennis" | "padel")[] || ["tennis"];
      setSelectedClubSports(clubSports);
      setSignupData(prev => {
        const validSports = prev.sports.filter(s => clubSports.includes(s));
        return {
          ...prev,
          sports: clubSports.length === 1 ? clubSports : (validSports.length > 0 ? validSports : [clubSports[0]])
        };
      });
    }
  }, [signupData.clubId, clubs]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);
      
      const roles = roleData?.map(r => r.role) || [];
      if (roles.includes('super-admin') || roles.includes('husselaar')) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    trackLoginStart();
    
    const validation = loginSchema.safeParse(loginData);
    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast.error(firstError.message);
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: validation.data.email,
        password: validation.data.password,
      });

      if (error) throw error;
      
      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', data.user.id);
      
      const roles = rolesData?.map(r => r.role) || [];
      
      const { data: profileData } = await supabase
        .from('profiles')
        .select('first_name')
        .eq('user_id', data.user.id)
        .single();

      const isAdminRole = roles.includes('super-admin') || roles.includes('club-beheerder');
      const redirectTo = roles.includes('super-admin') || roles.includes('husselaar') ? '/admin' : '/dashboard';

      if (isAdminRole) {
        const { data: twoFactorData } = await supabase
          .from('user_two_factor_status')
          .select('method, is_enabled, is_verified')
          .eq('user_id', data.user.id)
          .eq('is_enabled', true)
          .eq('is_verified', true)
          .single();

        if (twoFactorData) {
          navigate('/2fa-verify', {
            state: {
              userId: data.user.id,
              email: data.user.email,
              userName: profileData?.first_name,
              method: twoFactorData.method,
              redirectTo,
            },
          });
          return;
        } else {
          toast.info(t('account.twoFactorRequired'));
          navigate('/account', { state: { setupRequired: true } });
          return;
        }
      }
      
      trackLogin('email');
      toast.success(t('auth.loggedIn'));
      navigate(redirectTo);
    } catch (error: any) {
      toast.error(error.message || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!signupData.confirmMember) {
      toast.error(t('auth.confirmMemberError'));
      return;
    }

    if (!signupData.acceptTerms) {
      toast.error(t('auth.acceptTermsError'));
      return;
    }

    if (!croppedPhoto) {
      toast.error(t('auth.profilePhotoRequired'));
      return;
    }

    const validation = signupSchema.safeParse(signupData);
    if (!validation.success) {
      const firstError = validation.error.errors[0];
      toast.error(firstError.message);
      return;
    }

    setLoading(true);

    let createdUserId: string | null = null;
    
    try {
      let authData: { user: { id: string } | null } | null = null;
      const signUpAttempts = 0;

      const attemptSignUp = async () => {
        const result = await supabase.auth.signUp({
          email: signupData.email,
          password: signupData.password,
          options: {
            emailRedirectTo: `${window.location.origin}/dashboard`
          }
        });
        return result;
      };

      let result = await attemptSignUp();

      // If user already exists, try to clean up orphaned signup (no profile = incomplete registration)
      if (result.error && (result.error.message?.includes('User already registered') || (result.error as any)?.code === 'user_already_exists')) {
        console.log("User already exists, attempting orphaned signup cleanup...");
        
        const cleanupResponse = await supabase.functions.invoke('cleanup-orphaned-signup', {
          body: { email: signupData.email }
        });

        if (cleanupResponse.data?.cleaned) {
          // Orphaned user was cleaned up, retry signup
          console.log("Orphaned signup cleaned up, retrying...");
          result = await attemptSignUp();
        } else if (cleanupResponse.data?.reason === 'has_profile') {
          throw new Error("Dit e-mailadres is al in gebruik. Probeer in te loggen of gebruik een ander e-mailadres.");
        }
      }

      if (result.error) throw result.error;
      if (!result.data?.user) throw new Error("Geen gebruiker aangemaakt");
      
      authData = result.data;
      createdUserId = authData.user!.id;

      let photoPath = null;
      if (croppedPhoto) {
        const { compressImage } = await import("@/lib/image-compression");
        const compressed = await compressImage(croppedPhoto);
        const fileName = `${authData.user!.id}/${Date.now()}.jpg`;
        
        const { error: uploadError } = await supabase.storage
          .from('profile-photos')
          .upload(fileName, compressed, { contentType: 'image/jpeg' });

        if (uploadError) {
          console.error("Foto upload mislukt:", uploadError);
        } else {
          photoPath = fileName;
        }
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          user_id: authData.user!.id,
          first_name: signupData.firstName,
          last_name: signupData.lastName,
          gender: signupData.gender,
          skill_level: parseInt(signupData.skillLevel),
          birth_date: signupData.birthDate,
          club_id: signupData.clubId || null,
          profile_photo_url: photoPath,
          sports: signupData.sports
        });

      if (profileError) {
        console.error("Profile creation failed, cleaning up auth user:", profileError);
        await supabase.auth.signOut();
        throw new Error("Fout bij aanmaken profiel. Probeer opnieuw te registreren.");
      }

      trackSignUp('email', signupData.clubId);
      trackProfilePhotoUpload();
      toast.success(t('auth.accountCreated'));
      
      const { error: loginError } = await supabase.auth.signInWithPassword({
        email: signupData.email,
        password: signupData.password,
      });

      if (!loginError) {
        trackLogin('email');
        navigate('/dashboard');
      }
    } catch (error: any) {
      const { logRealtimeError } = await import("@/lib/error-logger");
      void logRealtimeError({
        flow: "player-signup-in-club",
        message: error?.message || "Speler signup faalde",
        stack: error?.stack,
      });
      toast.error(error.message || t('auth.registerError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfilePhoto(file);
      const objectUrl = URL.createObjectURL(file);
      setTempImageSrc(objectUrl);
      setCropDialogOpen(true);
    }
  };

  const handleCropComplete = (blob: Blob) => {
    setCroppedPhoto(blob);
    if (tempImageSrc) {
      URL.revokeObjectURL(tempImageSrc);
      setTempImageSrc(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center gap-3 mb-6">
            <div className="flex items-center gap-3">
              <img 
                src="/hussel-app-logo-2027-192.png" 
                alt="De Hussel App" 
                className="h-12 w-12 rounded-lg"
              />
              <h1 className="text-3xl font-bold text-foreground">{branding.name}</h1>
            </div>
            <StatusIndicator />
          </div>

          {searchParams.get('registration') === 'success' && searchParams.get('trial') === '1' && (
            <div className="mb-4 rounded-lg border border-accent/40 bg-accent/10 p-4 text-sm">
              <p className="font-semibold text-foreground">🎉 Je club staat klaar — gratis t/m 31 oktober 2026.</p>
              <p className="text-muted-foreground mt-1">Log hieronder in om direct te starten met je eerste hussel- of tossavond.</p>
            </div>
          )}

          <Card className="p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="auth-tab-login">{t('auth.login')}</TabsTrigger>
                <TabsTrigger value="signup" data-testid="auth-tab-signup">{t('auth.register')}</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('auth.noAccount')}{" "}
                    <button
                      type="button"
                      onClick={() => setActiveTab("signup")}
                      className="text-primary hover:underline"
                    >
                      {t('auth.createAccount')}
                    </button>
                  </p>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-email">{t('auth.email')}</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email"
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">{t('auth.password')}</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password"
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                    {loading ? t('auth.loggingIn') : t('auth.login')}
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    className="w-full mt-2"
                    onClick={async () => {
                      const email = prompt(t('auth.forgotPasswordPrompt'));
                      if (email) {
                        trackPasswordResetRequest();
                        const { error } = await supabase.auth.resetPasswordForEmail(email, {
                          redirectTo: `${window.location.origin}/reset-password`,
                        });
                        if (error) {
                          toast.error(t('auth.passwordResetError'));
                        } else {
                          toast.success(t('auth.checkEmail'));
                        }
                      }
                    }}
                  >
                    {t('auth.forgotPassword')}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <div className="mb-6 p-4 bg-accent/30 border border-accent/40 rounded-lg">
                  <p className="text-sm text-foreground">
                    {t('auth.registerIntro')}
                  </p>
                </div>
                
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">{t('auth.firstName')} *</Label>
                      <Input
                        id="firstName"
                        value={signupData.firstName}
                        onChange={(e) => setSignupData({ ...signupData, firstName: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">{t('auth.lastName')} *</Label>
                      <Input
                        id="lastName"
                        value={signupData.lastName}
                        onChange={(e) => setSignupData({ ...signupData, lastName: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-email">{t('auth.email')} *</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      value={signupData.email}
                      onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="gender">{t('auth.gender')} *</Label>
                      <Select
                        value={signupData.gender}
                        onValueChange={(value) => setSignupData({ ...signupData, gender: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('auth.select')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="M">{t('auth.genderMale')}</SelectItem>
                          <SelectItem value="V">{t('auth.genderFemale')}</SelectItem>
                          <SelectItem value="O">{t('auth.genderOther')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="skillLevel">{t('auth.skillLevel')} *</Label>
                      <Select
                        value={signupData.skillLevel}
                        onValueChange={(value) => setSignupData({ ...signupData, skillLevel: value })}
                        required
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="1-9" />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((level) => (
                            <SelectItem key={level} value={level.toString()}>
                              {level}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="birthDate">{t('auth.birthDate')} *</Label>
                    <Input
                      id="birthDate"
                      type="date"
                      value={signupData.birthDate}
                      onChange={(e) => setSignupData({ ...signupData, birthDate: e.target.value })}
                      required
                    />
                  </div>

                  <ClubSearchSelect
                    clubs={clubs}
                    value={signupData.clubId}
                    onChange={(clubId) => setSignupData({ ...signupData, clubId })}
                    placeholder="Zoek je club..."
                  />

                  {signupData.clubId && selectedClubSports.length > 1 && (
                    <div className="space-y-2">
                      <Label>{t('auth.whichSports')}</Label>
                      <div className="flex gap-4">
                        {selectedClubSports.includes("tennis") && (
                          <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border border-input hover:bg-accent/50 transition-colors">
                            <Checkbox
                              checked={signupData.sports.includes("tennis")}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSignupData({ ...signupData, sports: [...signupData.sports, "tennis"] });
                                } else if (signupData.sports.length > 1) {
                                  setSignupData({ ...signupData, sports: signupData.sports.filter(s => s !== "tennis") });
                                }
                              }}
                            />
                            <span>🎾 Tennis</span>
                          </label>
                        )}
                        {selectedClubSports.includes("padel") && (
                          <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg border border-input hover:bg-accent/50 transition-colors">
                            <Checkbox
                              checked={signupData.sports.includes("padel")}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSignupData({ ...signupData, sports: [...signupData.sports, "padel"] });
                                } else if (signupData.sports.length > 1) {
                                  setSignupData({ ...signupData, sports: signupData.sports.filter(s => s !== "padel") });
                                }
                              }}
                            />
                            <span>🏸 Padel</span>
                          </label>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{t('account.selectMin1Sport')}</p>
                    </div>
                  )}

                  {signupData.clubId && selectedClubSports.length === 1 && (
                    <div className="p-3 bg-accent/20 rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        {t('auth.sport')} <span className="font-medium text-foreground">
                          {selectedClubSports[0] === "tennis" ? "🎾 Tennis" : "🏸 Padel"}
                        </span>
                      </p>
                    </div>
                  )}

                  <div className="flex items-start gap-3 p-3 bg-accent/20 rounded-lg">
                    <Checkbox
                      id="confirmMember"
                      checked={signupData.confirmMember}
                      onCheckedChange={(checked) => setSignupData({ ...signupData, confirmMember: checked === true })}
                    />
                    <Label htmlFor="confirmMember" className="text-sm leading-relaxed cursor-pointer">
                      {t('auth.confirmMemberFull')}
                    </Label>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-accent/20 rounded-lg">
                    <Checkbox
                      id="acceptTerms"
                      checked={signupData.acceptTerms}
                      onCheckedChange={(checked) => setSignupData({ ...signupData, acceptTerms: checked === true })}
                    />
                    <Label htmlFor="acceptTerms" className="text-sm leading-relaxed cursor-pointer">
                      {t('auth.acceptTermsFull')}{" "}
                      <a 
                        href="/algemene-voorwaarden" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {t('auth.termsOfService')}
                      </a>
                      {" "}{t('auth.andThe')}{" "}
                      <a 
                        href="/privacy" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {t('auth.privacyPolicy')}
                      </a>
                    </Label>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="photo">{t('auth.profilePhoto')} *</Label>
                    {croppedPhoto && (
                      <div className="flex justify-center mb-2">
                        <img 
                          src={URL.createObjectURL(croppedPhoto)} 
                          alt="Preview" 
                          className="w-24 h-24 rounded-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Input
                        id="photo"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('photo')?.click()}
                        className="w-full"
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        {croppedPhoto ? t('auth.changePhoto') : t('auth.uploadPhoto')}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">{t('auth.photoCropHint')}</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-password">{t('auth.password')} *</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      value={signupData.password}
                      onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm">{t('auth.confirmPassword')} *</Label>
                    <Input
                      id="signup-confirm"
                      type="password"
                      value={signupData.confirmPassword}
                      onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? t('auth.registering') : t('auth.createAccountButton')}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </div>
      <Footer hideSiteLinks />
      
      {tempImageSrc && (
        <ImageCropDialog
          open={cropDialogOpen}
          onClose={() => {
            setCropDialogOpen(false);
            if (tempImageSrc) {
              URL.revokeObjectURL(tempImageSrc);
              setTempImageSrc(null);
            }
          }}
          imageSrc={tempImageSrc}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
};

export default Auth;
